import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import JourneySummary from "../components/Summary/JourneySummary";
import Popup from "../components/Modals/popup";
import Box from "../components/Box/Box";
import Spinner from "../components/Spinner/Spinner";
import useFocusOnLoad from "../hooks/useFocusOnLoad";
import { useUnregisterFlow } from "../hooks/useUnregisterFlow";
import { createJourney } from "../utils/journeyHelpers";
import { getTravelPlanDetailsAPI as getTravelPlanDetails } from "../api/travelPlan";
import { cancelGroupTravelAPI } from "../api/unregister";

export default function UnregisterTravelSummary() {
  const h2Ref = useFocusOnLoad<HTMLHeadingElement>();
  const navigate = useNavigate();
  const hasRedirected = useRef(false);
  const [journeys, setJourneys] = useState<any[]>([]);
  const [showPopup, setShowPopup] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { saveUnregisterData, unregisterData } = useUnregisterFlow();
  const [searchParams] = useSearchParams();
  const [hasNoJourneyData, setHasNoJourneyData] = useState(false);

  useEffect(() => {
    
    const ReferenceId = searchParams.get("ref");
    const EditId = searchParams.get("id");

    if (!ReferenceId || !EditId) {
      if (!hasRedirected.current) {
        hasRedirected.current = true;
        navigate("/avboka-resa");
      }
      setIsLoading(false);
      return;
    }
  
    
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const result = await getTravelPlanDetails(ReferenceId, EditId);
  
        const hasValidJourney =
          result?.selectedJourney &&
          result?.selectedJourney?.fromName &&
          result.ReferenceId &&
          result.EditId;

        if (!hasValidJourney) {
          setHasNoJourneyData(true);
          return;
        }
  
        saveUnregisterData({
          ReferenceId: result.ReferenceId,
          EditId: result.EditId,
          email: result.email,
        });
     
        const newJourneys = [createJourney(result.selectedJourney, "Resa dit", "")];
  
        if (result.selectedReturnJourney?.fromName) {
          newJourneys.push(createJourney(result.selectedReturnJourney, "Resa tillbaka", ""));
        }
  
        setJourneys(newJourneys);
      } catch (error) {
        console.error("Fel vid hämtning av gruppresa:", error);
        navigate("/avboka-resa-fel");
      } finally {
        setIsLoading(false);
      }
    };
  
    fetchData();
  }, [searchParams, navigate, saveUnregisterData]);
  

  const handleConfirmUnregister = async () => {
    setShowPopup(false);
  
    if (!unregisterData?.ReferenceId || !unregisterData?.EditId) {
      navigate("/avboka-resa-fel");
      return;
    }
  
    try {
      const data = await cancelGroupTravelAPI(
        unregisterData.ReferenceId,
        unregisterData.EditId
      );
  
      saveUnregisterData({
        ReferenceId: unregisterData.ReferenceId,
        EditId: unregisterData.EditId,
        email: unregisterData.email ?? "",
      });

      navigate("/avboka-resa-gemomford");
    } catch (e) {
      navigate("/avboka-resa-fel");
    }
  };
  

  return (
    <div>
      <Box>
        <h2 ref={h2Ref}>Avanmäl planerad gruppresa</h2>

        <p>
            Här kan ni avanmäla er planerade gruppresa. Då avanmäler ni både resan dit och resan
            tillbaka. Om ni i stället vill ändra något eller bara avanmäla en av resorna kan ni ringa till oss
            på {" "}
            <a href="tel:+46771211010" className="text-red-darker">
              0771-21 10 10
          </a>
        </p>

        <h3 className="mt-4 mb-4 text-lg font-semibold">Föranmälda gruppresor</h3>

        <div className="flex px-4 py-3 sans-bold text-lg bg-red text-white">
          Referensnummer: {unregisterData?.ReferenceId ?? "..."}
        </div>

        <div className="summary-section patient-summary border-0 pb-0">
          <div className="summary-data-wrapper flex flex-col">
                {isLoading ? (
                    <Spinner></Spinner>
                ) : hasNoJourneyData || journeys.length === 0 ? (
                    <p className="mt-4  ">
                        Vi kunde tyvärr inte läsa in någon resa just nu. Vänligen försök igen senare eller kontakta kundservice.
                    </p>
                ) : (
                    journeys.map((journey, idx) => (
                        <JourneySummary
                            key={idx}
                            journey={journey}
                            isReturnTravel={journey.Headline.includes("Återresa")}
                            editPath=""
                            showEditButton={false}
                            showRemoveButton={false}
                        />
                    ))
                )}
          </div>
        </div>
      </Box>

      <div className="flex mt-6">
        <button
          className={`btn primary ${journeys.length === 0 ? "hide" : ""}`}
          onClick={() => setShowPopup(true)}
          disabled={journeys.length === 0}
        >
          Avanmäl gruppresa
        </button>
      </div>

      {showPopup && (
        <Popup
          isOpen={showPopup}
          title="Bekräfta avanmälan"
          description="Vi vill bara dubbelkolla. Vill ni inte göra den här resan? När den är borttagen kan andra göra en anmälan till den. Ni är självklart välkomna att anmäla en ny gruppresa."
          onConfirm={handleConfirmUnregister}
          onCancel={() => setShowPopup(false)}
          cancelText="Avbryt"
          confirmText="Avanmäl gruppresa"
        />
      )}
    </div>
  );
}
